<template>
  <div class="container">
    <div class="row mt-3">
      <div class="col-lg-4 col-12">
        <button class="btn btn-link btn-back" @click="backToCalendar"><i class="fa fa-angle-left"></i> Terug</button>
      </div>
    </div>
    <div class="row mt-3" v-if="buddy !== null">
      <div class="col-lg-4 col-12">
        <BuddySelectButton :buddy="buddy"></BuddySelectButton>
      </div>
    </div>
  </div>
  <div class="container content-block my-3" v-if="fetchingCalendarEvent">
    <div class="row">
      <div class="col text-center">
        <div class="alert alert-info" role="alert">
          Event ophalen.
        </div>
        <div class="d-flex justify-content-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Laden...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="calendarEvent == null">
    <NotFound></NotFound>
  </div>
  <div class="container my-3" v-else>
    <div class="row">
      <div class="col-4">
        <div class="container content-block event-panel">
          <div class="row">
            <div class="col">
              <h2>Herinnering inplannen</h2>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col">
              <div class="property-block event-date-group">
                <i class="far fa-calendar-alt"></i>
                <DateSelector class="form-control event-date-input" v-if="editing"
                              v-model:date="calendarEvent.date"></DateSelector>
                <span class="event-date ml-3" v-else>{{ formatDate(calendarEvent.date) }}</span>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col">
              <div class="property-block event-title">
                <input class="form-control event-title-input" type="text" v-if="editing"
                       v-model="calendarEvent.title" placeholder="Vb.: Pedagogische studiedag"/>
                <span v-else>{{ calendarEvent.title }}</span>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col">
              <VButton @click="saveCalendarEvent" v-if="editing" :enabled="isValid" :buttonText="'Herinnering opslaan'">
              </VButton>
              <VButton @click="editCalendarEvent" v-else :enabled="isValid" :buttonText="'Herinnering wijzigen'">
              </VButton>
            </div>
          </div>
          <div class="row mt-1" v-if="editing && calendarEventId">
            <div class="col text-center">
              <button class="btn text-button orange" @click="cancelEdit">Annuleren</button>
            </div>
          </div>
          <div class="row mt-1" v-else-if="calendarEventId">
            <div class="col text-center">
              <button class="btn text-button orange" @click="deleteCalendarEvent">Herinnering verwijderen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CalendarEventApi, CalendarEvent, BuddyApi } from "sandpiper_api";
import log from "@/utils/logger";
import dateFormatter from "@/mixins/DateFormatter";
import BuddySelectButton from "@/components/Common/BuddySelectButton";
import QueryParamDateHelper from "@/mixins/QueryParamDateHelper";
import DateSelector from "@/components/UI/DateSelector";
import NotFound from "@/components/Common/NotFound";
import VButton from "@/components/UI/VButton";

export default {
  name: "EventEdit",
  components: { DateSelector, BuddySelectButton, NotFound, VButton },
  mixins: [ dateFormatter, QueryParamDateHelper ],
  inject: [ 'doApiRequest' ],
  data()
  {
    return {
      fetchingBuddy: false,
      buddyId: null,
      buddy: null,

      fetchingCalendarEvent: false,
      updatingCalendarEvent: false,
      editing: false,
      calendarEventId: null,
      calendarEvent: null,

      initialDate: new Date()
    }
  },
  computed: {
    isValid()
    {
      if (!this.calendarEvent || !this.calendarEvent.date || !this.calendarEvent.title)
      {
        return false;
      }

      return true;
    },
  },
  methods: {
    fetchPathParams()
    {
      this.calendarEventId = this.$route.params.id;
    },
    fetchQueryParams()
    {
      this.buddyId = this.$route.query.buddyId;

      this.initialDate = this.parseDateFromQueryParam(this.$route.query.initialDate);
      if (this.calendarEvent && !this.calendarEvent.date)
      {
        this.calendarEvent.date = this.initialDate;
      }
    },

    backToCalendar()
    {
      this.$router.push({ name: "agenda" });
    },
    async fetchBuddy()
    {
      try
      {
        log.info("Fetching buddy");
        this.fetchingBuddy = true;
        this.buddy = null;
        this.buddy = await this.doApiRequest(() => new BuddyApi().getBuddy(this.buddyId));
        this.buddy.selected = true;
      }
      catch (e)
      {
        // TODO - handle error
        log.error("Failed to fetch buddy - %o", e);
      }
      finally
      {
        this.fetchingBuddy = false;
      }
    },
    async fetchCalendarEvent()
    {
      try
      {
        this.fetchingCalendarEvent = true;
        this.calendarEvent = await this.doApiRequest(
            () => new CalendarEventApi().getCalendarEvent(this.calendarEventId));
      }
      catch (e)
      {
        this.calendarEvent = null;
        // TODO - handle exception, return to calendar event overview?
        // await this.$router.push({name: 'events'});
      }
      finally
      {
        this.fetchingCalendarEvent = false;
      }
    },
    editCalendarEvent()
    {
      this.editing = true;
    },
    cancelEdit()
    {
      this.editing = false;
    },
    async saveCalendarEvent()
    {
      log.debug("Saving calendar event: %o", this.calendarEvent);

      // Note - bug with javascript openapi generator. Date doesn't get formatted correctly
      // When type is a DATE object (without time!), the date will be serialized as a ISO formatted date time (with offset).
      // This will then cause the java to deserialize the date as the UTC date instead of the date the user select.
      // We can work around this by formatting the date ourselves.
      let calendarEventRequestObject = Object.assign({}, this.calendarEvent);
      calendarEventRequestObject.date = this.apiDateFormat(calendarEventRequestObject.date);

      try
      {
        this.updatingCalendarEvent = true;
        if (this.calendarEventId)
        {
          this.calendarEvent = await this.doApiRequest(
              () => new CalendarEventApi().updateCalendarEvent(this.calendarEventId, calendarEventRequestObject));

          log.info("Updated calendar event: %o", this.calendarEvent);
        }
        else
        {
          this.calendarEvent = await this.doApiRequest(
              () => new CalendarEventApi().createCalendarEvent(calendarEventRequestObject));
        }

        await this.$router.push({ name: "agenda" });
      }
      catch (e)
      {
        this.calendarEvent = null;
        // TODO - handle exception, return to calendar event overview?
        // await this.$router.push({name: 'events'});
      }
      finally
      {
        this.updatingCalendarEvent = false;
      }
    },
    async deleteCalendarEvent()
    {
      if (this.updatingCalendarEvent)
      {
        log.debug(`Already updating calendar event, halting this call`);
        return;
      }

      if (confirm("Ben je zeker dat je dit event wilt verwijderen?"))
      {
        try
        {
          this.updatingCalendarEvent = true;

          const response = await this.doApiRequest(
              () => new CalendarEventApi().deleteCalendarEvent(this.calendarEventId));
          log.info("Delete calendar event response: %o", response);
          await this.$router.push({ name: "agenda" });
        }
        catch (e)
        {
          // TODO - handle exception, popup with message?
        }
        finally
        {
          this.updatingCalendarEvent = false;
        }
      }
    },
    loadData()
    {
      if (this.calendarEventId)
      {
        this.fetchCalendarEvent();
      }
      else
      {
        this.calendarEvent = new CalendarEvent();
        this.calendarEvent.date = this.initialDate || new Date();
        this.calendarEvent.buddyId = this.buddyId;
        this.editing = true;
      }

      this.fetchBuddy();
    }
  },
  created()
  {
    this.fetchPathParams();
    this.fetchQueryParams();
    this.loadData();
  }
}
</script>

<style lang="scss" scoped>
button.btn-back
{
  color: $font-dark;
  text-decoration: none;
  font-weight: bold;

  i
  {
    color: $primary-blue;
    margin-right: 0.5rem;
  }

  &:hover
  {
    text-decoration: underline;
  }
}

.event-panel
{
  padding: 25px 15px;
}

.property-block
{
  background: white;
  padding: 5px 15px;
  border-radius: 5px;
  box-shadow: $default-shadow;

  font-weight: bold;

  min-height: 50px;

  display: flex;
  flex-direction: row;
  align-items: center;
}

.event-date-input
{
  border: none;
}

.event-title-input
{
  padding-left: 0;
  border: none;
}
</style>